import React, { useContext, useState } from "react"
import { useForm } from "react-hook-form"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { MAPPED, UNMAPPED } from "src/main/Accounting/constants"

import Badge from "src/components/Badge"
import { statusToken } from "src/components/Badge/status_tokens"
import Button from "src/components/Button"
import Chips from "src/components/Chips"
import DataTable from "src/components/DataTable"
import Form from "src/components/Form"
import Loader from "src/components/Loader"
import OverflowMenu from "src/components/OverflowMenu"

import { queryInvoiceItems, updateInvoiceItem } from "src/api/Apideck"
import { queryMappableDockwaProducts } from "src/api/Integrations"

import useDebounce from "src/hooks/use_debounce"
import { useToast } from "src/hooks/use_toast"
import useWindowSize from "src/hooks/use_window_size"

import { formatCurrencyFromMinorUnits } from "src/utils/UnitConversion"
import { snakecaseToCamelCase, titlecase } from "src/utils/string_helpers"
import { updateUrlQueryParams } from "src/utils/url/parsing/marina"

import { AccountingContext } from "./AccountingView"
import VirtualizedInvoiceItemList from "./VirtualizedInvoiceItemList"

const EMPTY_NO_FILTERS =
  "No products match the selected filters, please try adjusting your selection(s)."
const EMPTY_WITH_FILTERS = "No products available to be mapped at this time."
const PAGE_SIZE = 25

const ItemMappingTab = () => {
  const {
    categoryOptions,
    integration,
    marinaSlug,
    selectedAccountingService,
  } = useContext(AccountingContext)
  const queryClient = useQueryClient()
  const showToast = useToast()
  const urlParams = new URLSearchParams(window.location.search)
  const [queryableInvoiceItems, setQueryableInvoiceItems] = useState([])
  const [statusMenuOpen, setStatusMenuOpen] = useState(false)
  const [filters, setFilters] = useState({
    categories:
      urlParams.getAll("categories").length > 0
        ? urlParams.getAll("categories")[0].split(",")
        : [],
    page: urlParams.get("page") || 1,
    searchTerm: urlParams.get("search_term") || "",
    sortDirection: urlParams.get("sort_direction") || "asc",
    sortKey: urlParams.get("sort_key")
      ? snakecaseToCamelCase(urlParams.get("sort_key"))
      : "status",
    status: urlParams.get("status") || "",
  })
  const { categories, searchTerm, sortDirection, sortKey, status } = filters
  const hasFiltersApplied = categories.length > 0 || !!searchTerm || !!status
  const [debouncedQuery] = useDebounce(setFilters)
  const { isXLScreen } = useWindowSize()

  const updateParams = (params) => {
    debouncedQuery(params)
    updateUrlQueryParams({
      categories: params.categories.join(","),
      search_term: params.searchTerm,
      sort_key: params.sortKey,
      sort_direction: params.sortDirection,
      status: params.status,
      page: params.page,
    })
  }

  const handleCategoryFilterChange = (event) => {
    const { id, checked } = event.target
    const updatedCategories = checked
      ? [...categories, id]
      : categories.filter((category) => category !== id)
    setValue(id, checked)
    updateParams({ ...filters, categories: updatedCategories, page: 1 })
  }

  const handleColumnSort = (key) => {
    let newSortDirection = null
    let newSortKey = key
    if (sortKey === key) {
      if (sortDirection === "asc") {
        newSortDirection = "desc"
      } else {
        newSortKey = null
      }
    } else {
      newSortDirection = "asc"
    }

    updateParams({
      ...filters,
      sortKey: newSortKey,
      sortDirection: newSortDirection,
      page: 1,
    })
  }

  const handleFilterInvoiceItems = (event) => {
    const { value } = event.target
    setValue("filterQueryableInvoiceItems", value)
    setQueryableInvoiceItems(
      invoiceItems.filter((invoiceItem) =>
        invoiceItem.name.toLowerCase().includes(value.toLowerCase())
      )
    )
  }

  const handlePageChange = (page) => {
    updateParams({ ...filters, page: page.selected })
  }

  const handleResetFilters = () => {
    categoryOptions.forEach(([categoryId, categoryLabel]) => {
      setValue(categoryId, false)
    })
    updateParams({
      ...filters,
      categories: [],
      searchTerm: "",
      status: "",
      page: 1,
    })
  }

  const handleSearch = (event) => {
    setValue("searchTerm", event.target.value)
    updateParams({ ...filters, searchTerm: event.target.value, page: 1 })
  }

  const handleStatusFilterChange = (event) => {
    const { name, checked } = event.target

    setValue("mapped", name === MAPPED ? checked : false)
    setValue("unmapped", name === UNMAPPED ? checked : false)

    setStatusMenuOpen(false)
    const statusValue = checked ? name : ""
    updateParams({ ...filters, status: statusValue, page: 1 })
  }

  const resetQueryableInvoiceItems = () => {
    setValue("filterQueryableInvoiceItems", "")
    setQueryableInvoiceItems(invoiceItems)
  }

  const { register, setValue, watch } = useForm({
    defaultValues: {
      mapped: filters.status === MAPPED,
      filterQueryableInvoiceItems: "",
      searchTerm,
      unmapped: filters.status === UNMAPPED,
      ...Object.fromEntries(
        categoryOptions.map(([categoryId, categoryLabel]) => [
          categoryId,
          categories.includes(categoryId),
        ])
      ),
    },
  })

  const COLDEFS = [
    {
      key: "status",
      header: "Status",
      render: (data) => (
        <Badge
          color={statusToken(data.apideckInvoiceItem ? MAPPED : UNMAPPED)}
          text={
            data.apideckInvoiceItem ? titlecase(MAPPED) : titlecase(UNMAPPED)
          }
        />
      ),
      sortable: true,
      width: "10%",
    },
    {
      key: "product",
      header: "Dockwa Product",
      render: (data) => data.name,
      sortable: true,
      width: "30%",
    },
    {
      key: "category",
      header: "Category",
      render: (data) => data.productCategoryName,
      sortable: true,
      width: "20%",
    },
    {
      key: "price",
      header: "Price",
      render: (data) =>
        data.pricePerUnit
          ? formatCurrencyFromMinorUnits({
              amount: data.pricePerUnit,
              precision: data.pricePrecision === "cents" ? 2 : 4,
            })
          : null,
      sortable: true,
      width: "10%",
    },
    {
      key: "product",
      header: "Accounting Item",
      render: (data) => (
        <OverflowMenu
          menuButtonVariant="quarternary"
          menuButtonLabel={data.apideckInvoiceItem?.name || "Select"}
          menuItemWidth="450px"
          menuButtonFullWidth
        >
          <div className="p-2">
            <Form.IconTextField
              id="filter-invoice-items"
              icon={
                <i className="fa-solid fa-magnifying-glass text-gray-600" />
              }
              position="left"
              value={watch("filterQueryableInvoiceItems")}
              isClearable
              placeholder="Search items"
              onBlur={resetQueryableInvoiceItems}
              onChange={handleFilterInvoiceItems}
              onClearSelection={resetQueryableInvoiceItems}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </div>
          <div data-testid={`product-${data.id}-mapping-select`}>
            <VirtualizedInvoiceItemList
              queryableInvoiceItems={queryableInvoiceItems}
              currentProduct={data}
              apideckInvoiceItem={data.apideckInvoiceItem}
              isMutatingInvoiceItem={isMutatingInvoiceItem}
              mutateInvoiceItem={mutateInvoiceItem}
            />
          </div>
        </OverflowMenu>
      ),
      sortable: false,
      width: "30%",
    },
  ]

  const sortConfig = {
    sortKey: sortKey ? snakecaseToCamelCase(sortKey) : null,
    sortDirection: sortDirection || null,
    onColumnSort: handleColumnSort,
  }

  const { isLoading: isLoadingInvoiceItems, data: invoiceItems } = useQuery({
    queryKey: ["invoice_items", marinaSlug],
    queryFn: () => {
      return queryInvoiceItems(marinaSlug)
    },
    onSuccess: (data) => setQueryableInvoiceItems(data),
    refetchOnWindowFocus: false,
    enabled: !!integration?.setupComplete,
    retry: false,
  })

  const { isLoading: isLoadingDockwaProducts, data: dockwaProducts } = useQuery(
    {
      queryKey: ["products", marinaSlug, filters],
      queryFn: () =>
        queryMappableDockwaProducts({
          marinaSlug,
          filters,
          pageSize: PAGE_SIZE,
        }),
      refetchOnWindowFocus: false,
      enabled: !!integration?.setupComplete,
      retry: false,
    }
  )

  const { mutate: mutateInvoiceItem, isLoading: isMutatingInvoiceItem } =
    useMutation(({ id, data }) => updateInvoiceItem({ marinaSlug, id, data }), {
      onSuccess: (data) => {
        resetQueryableInvoiceItems()
        queryClient.invalidateQueries("invoice_items")
        queryClient.invalidateQueries("products")
        showToast("Item mapping updated successfully!", {
          type: "success",
          duration: 3,
        })
      },
      onError: (error) => {
        showToast(error.toString(), { type: "error", duration: 5 })
      },
    })

  const renderCategoryFilter = () => (
    <OverflowMenu menuButtonLabel="Category" menuButtonFullWidth={!isXLScreen}>
      <div className="space-y-2 p-4">
        {categoryOptions.map(([categoryId, categoryLabel]) => {
          return (
            <Form.Checkbox
              {...register(categoryId)}
              key={categoryId}
              label={categoryLabel}
              onChange={handleCategoryFilterChange}
            />
          )
        })}
      </div>
    </OverflowMenu>
  )

  const renderFilterChips = () => {
    return (
      <div className="flex flex-wrap">
        {searchTerm && (
          <Chips.Chip
            label="Search"
            text={`Search: ${searchTerm}`}
            onClick={() => {
              setValue("searchTerm", "")
              updateParams({ ...filters, searchTerm: "", page: 1 })
            }}
          />
        )}
        {categories.map((appliedCategoryFilterId) => {
          const [appliedCategoryId, appliedCategoryLabel] =
            categoryOptions.find(
              ([categoryId, categoryLabel]) =>
                categoryId === appliedCategoryFilterId
            )
          return (
            <Chips.Chip
              key={`category-chip-${appliedCategoryLabel}`}
              label="Category"
              text={`Category: ${appliedCategoryLabel}`}
              onClick={() => {
                setValue(appliedCategoryId, false)
                updateParams({
                  ...filters,
                  categories: categories.filter(
                    (categoryFilterId) => categoryFilterId !== appliedCategoryId
                  ),
                  page: 1,
                })
              }}
            />
          )
        })}
        {!!status && (
          <Chips.Chip
            key={`status-chip-${status}`}
            label="Status"
            text={`Status: ${titlecase(status)}`}
            onClick={() => {
              setValue(status, false)
              updateParams({ ...filters, status: "", page: 1 })
            }}
          />
        )}
        {hasFiltersApplied && (
          <div className="flex w-min items-center">
            <Button variant="ghost" small onClick={handleResetFilters}>
              Reset
            </Button>
          </div>
        )}
      </div>
    )
  }

  const renderSearchInput = () => (
    <Form.IconTextField
      {...register("searchTerm")}
      icon={
        <i className="fa-solid fa-magnifying-glass text-xl text-gray-400" />
      }
      id="invoice-item-search"
      isClearable
      onChange={handleSearch}
      onClearSelection={() => {
        setValue("searchTerm", "")
        updateParams({ ...filters, searchTerm: "" })
      }}
      position="left"
      value={watch("searchTerm")}
      placeholder="Search item name"
    />
  )

  const renderStatusFilter = () => (
    <OverflowMenu
      menuButtonLabel="Status"
      manualClose={true}
      menuOpen={statusMenuOpen}
      setMenuOpen={setStatusMenuOpen}
      menuButtonFullWidth={!isXLScreen}
    >
      <div className="space-y-2 p-4">
        <Form.Checkbox
          {...register(MAPPED)}
          key={MAPPED}
          label={titlecase(MAPPED)}
          onChange={handleStatusFilterChange}
        />
        <Form.Checkbox
          {...register(UNMAPPED)}
          key={UNMAPPED}
          label={titlecase(UNMAPPED)}
          onChange={handleStatusFilterChange}
        />
      </div>
    </OverflowMenu>
  )

  const renderTable = () => {
    if (isLoadingInvoiceItems || isLoadingDockwaProducts) {
      return (
        <div className="bg-white">
          <Loader name="item mappings" />
        </div>
      )
    } else if (dockwaProducts?.items?.length === 0) {
      return (
        <div className="my-5 text-center">
          {hasFiltersApplied ? EMPTY_NO_FILTERS : EMPTY_WITH_FILTERS}
        </div>
      )
    } else {
      return (
        <DataTable
          name="invoice-items"
          isLoading={isLoadingInvoiceItems || isLoadingDockwaProducts}
          colDefs={COLDEFS}
          rowData={dockwaProducts?.items}
          page={dockwaProducts?.page}
          numberOfPages={dockwaProducts?.page_count}
          onPageChange={handlePageChange}
          sortConfig={sortConfig}
          fullHeight
          hideActions
          hideSettings
          pagination
        />
      )
    }
  }

  return (
    <div className="mb-[250px]">
      <h4 className="mb-1 pt-6 font-semibold text-gray-900">Item Mapping</h4>
      <div className="mb-8">
        <span className="text-gray-800">
          Map your {selectedAccountingService?.name || "Accounting System"}{" "}
          items to Dockwa products.
        </span>
      </div>
      <div className="xl:no-wrap flex flex-wrap">
        <div className="w-full pb-2 xl:mr-2 xl:w-[336px] xl:p-0">
          {renderSearchInput()}
        </div>
        <div className="flex w-full xl:w-min">
          <div className="flex w-full flex-wrap space-y-2 sm:flex-nowrap sm:space-y-0">
            {renderCategoryFilter()}
            {renderStatusFilter()}
          </div>
        </div>
      </div>
      <div className="px-4 xl:px-0">
        <Chips.List>{renderFilterChips()}</Chips.List>
      </div>
      <div className="my-8">{renderTable()}</div>
    </div>
  )
}

export default ItemMappingTab
